import { Box, TextInput, Select, Text, Flex, Button, FileButton, Image, Tooltip, Grid } from "@mantine/core";
import { IconBuilding, IconLock } from "@tabler/icons-react";
import {
  HEADQUARTERS_COUNTRY_PHONE_ISO,
  THE_KYB_US_STATE_CODES,
} from "../../../components/shared/constants/general.const";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { useState, useEffect, useRef } from "react";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { errorNotification } from "@/shared/utils/errorNotification";
import {
  VERIFICATION_BUSINESS_TYPE_SELECT_DATA,
  isVerificationBusinessTypePreset,
} from "./verificationDocuments";

const PAIDUP_CAPITAL_BAND_SELECT_DATA = [
  { value: "0 - 250000", label: "0 - 250,000" },
  { value: "250001 - 1000000", label: "250,001 - 1,000,000" },
  { value: "Above 1000000", label: "Above 1,000,000" },
];

const TURNOVER_BAND_SELECT_DATA = [
  { value: "0 - 500000", label: "0 - 500,000" },
  { value: "500001 - 2000000", label: "500,001 - 2,000,000" },
  { value: "Above 2000000", label: "Above 2,000,000" },
];

interface BasicCompanyInfoProps {
  formData: {
    companyLegalName: string;
    tradeName: string;
    headquartersCity: string;
    headquartersCountry: string;
    registrationNumber: string;
    registrationIdType: string | null;
    companyWebsite: string;
    phoneNumber: string;
    state?: string;
    phoneCountryCode: string;
    verificationBusinessType?: string | null;
    paidupCapitalBand?: string;
    turnoverBand?: string;
  };
  updateForm: (field: string, value: any) => void;
  logo: File | null;
  setLogo: (file: File | null) => void;
  countryOptions: { group: string; items: { value: string; label: string }[] }[];
  existingLogo?: any;
  onRemoveLogo?: () => void;
  isFieldLocked?: (fieldName: string) => boolean;
  rightOfLogoContent?: React.ReactNode;
}

export default function BasicCompanyInfo({
  formData,
  updateForm,
  logo,
  setLogo,
  countryOptions,
  existingLogo,
  onRemoveLogo,
  isFieldLocked = () => false,
  rightOfLogoContent,
}: BasicCompanyInfoProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<() => void>(null);

  const lockedTooltip = "This field cannot be edited after verification";
  const normalizedCountry = formData.headquartersCountry?.trim().toLowerCase();
  const isUSA = normalizedCountry === "usa" || normalizedCountry === "united states";
  useEffect(() => {
    let mounted = true;

    const loadLogoPreview = async () => {
      if (logo) {
        const preview = URL.createObjectURL(logo);
        if (mounted) setLogoPreview(preview);
        return () => URL.revokeObjectURL(preview);
      } else if (existingLogo?.id) {
        try {
          const url = await getFileUrl(existingLogo.id);
          if (mounted) setLogoPreview(url);
        } catch (err) {
          console.error("Failed to load logo preview:", err);
          if (mounted) setLogoPreview(null);
        }
      } else {
        if (mounted) setLogoPreview(null);
      }
    };

    loadLogoPreview();

    return () => {
      mounted = false;
    };
  }, [logo, existingLogo]);

  const phoneDefaultCountry =
    (formData.headquartersCountry && HEADQUARTERS_COUNTRY_PHONE_ISO[formData.headquartersCountry]) || "in";

  const rawBusinessStructure = formData.verificationBusinessType ?? "";
  const businessStructureSelectValue = isVerificationBusinessTypePreset(rawBusinessStructure)
    ? rawBusinessStructure
    : rawBusinessStructure === ""
      ? null
      : "OTHER";
  const businessStructureOtherText =
    isVerificationBusinessTypePreset(rawBusinessStructure) || rawBusinessStructure === ""
      ? ""
      : rawBusinessStructure === "OTHER"
        ? ""
        : rawBusinessStructure;

  const renderLabel = (label: string, fieldName: string, required = false, extra?: React.ReactNode) => (
    <Flex align="center" gap={6} wrap="nowrap" style={{ minHeight: 20 }}>
      <Text component="span" fz="sm" fw={500} c="dark.7" lh={1.35} style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
        {label}
        {required ? (
          <Text component="span" c="red" fz="sm" fw={500} lh={1.35}>
            *
          </Text>
        ) : null}
        {extra}
      </Text>
      {isFieldLocked(fieldName) && (
        <Tooltip label={lockedTooltip} withArrow position="top">
          <Flex align="center" style={{ flexShrink: 0 }}>
            <IconLock size={12} color="#9CA3AF" />
          </Flex>
        </Tooltip>
      )}
    </Flex>
  );

  const fieldStyles = {
    root: { marginBottom: 0 },
    label: { marginBottom: 6 },
  } as const;

  return (
    <Box>
      {/* Company Logo - Always editable */}
      <Box mb="xl">
        <Text visibleFrom="md" fz="sm" fw={500} c="dark.7" mb={6}>
          Company Logo
        </Text>

        {/* Mobile: show boxes above logo */}
        {rightOfLogoContent && (
          <Box hiddenFrom="md" mb="md">
            {rightOfLogoContent}
          </Box>
        )}

        <Text hiddenFrom="md" fz="sm" fw={500} c="dark.7" mb={6}>
          Company Logo
        </Text>

        {/* Desktop: logo left, boxes right */}
        <Flex gap="md" align="center">
          <Box
            style={{
              width: 96,
              height: 96,
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid #04305c",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F9FAFB",
            }}
          >
            {logoPreview ? (
              <Image
                src={logoPreview}
                alt="Company Logo"
                fit="cover"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  console.error("❌ Logo failed to load:", logoPreview);
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <IconBuilding size={28} color="#6B7280" />
            )}
          </Box>
          <Box>
            <Flex gap="xs">
              <FileButton
                resetRef={logoInputRef}
                onChange={(file) => {
                  if (file) {
                    const maxSize = 2 * 1024 * 1024; // 2MB
                    if (file.size > maxSize) {
                      errorNotification({
                        title: "File too large",
                        message: "Logo size must be less than 2MB",
                      });
                      logoInputRef.current?.();
                      return;
                    }
                    setLogo(file);
                    logoInputRef.current?.();
                  }
                }}
                accept="image/png,image/jpeg"
              >
                {(props) => (
                  <Button variant="outline" size="xs" {...props}>
                    Upload Logo
                  </Button>
                )}
              </FileButton>

              {(logo || logoPreview) && (
                <Button
                  variant="outline"
                  size="xs"
                  color="red"
                  onClick={() => {
                    if (onRemoveLogo) {
                      onRemoveLogo();
                    } else {
                      setLogo(null);
                      setLogoPreview(null);
                    }
                  }}
                >
                  Remove
                </Button>
              )}
            </Flex>
            <Text fz="xs" c="slate.6" mt={6}>
              PNG, JPG up to 2MB. Recommended: 400×400px square logo
            </Text>
          </Box>
          {rightOfLogoContent && (
            <Box visibleFrom="md" style={{ marginLeft: "auto", minWidth: 260, maxWidth: "100%" }}>
              {rightOfLogoContent}
            </Box>
          )}
        </Flex>
      </Box>

      <Grid gutter="md" mb="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            styles={fieldStyles}
            label={renderLabel("Company Legal Name", "companyLegalName", true)}
            placeholder="Enter registered company name"
            value={formData.companyLegalName}
            onChange={(e) => updateForm("companyLegalName", e.currentTarget.value)}
            disabled={isFieldLocked("companyLegalName")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            styles={fieldStyles}
            label={renderLabel("Trade Name / DBA", "tradeName", false)}
            placeholder="Public brand name"
            value={formData.tradeName}
            onChange={(e) => updateForm("tradeName", e.currentTarget.value)}
            disabled={isFieldLocked("tradeName")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            styles={fieldStyles}
            label={renderLabel("Company Website", "companyWebsite", true)}
            placeholder="https://www.company.com"
            value={formData.companyWebsite}
            onChange={(e) => updateForm("companyWebsite", e.currentTarget.value)}
            disabled={isFieldLocked("companyWebsite")}
            error={
              formData.companyWebsite &&
                !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.companyWebsite)
                ? "Please enter a valid website URL"
                : null
            }
          />
        </Grid.Col>
      </Grid>

      <Grid gutter="md" mb="xs">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            styles={fieldStyles}
            label={renderLabel("City", "headquartersCity", true)}
            placeholder="Enter city"
            value={formData.headquartersCity}
            onChange={(e) => updateForm("headquartersCity", e.currentTarget.value)}
            disabled={isFieldLocked("headquartersCity")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Select
            styles={fieldStyles}
            label={renderLabel("Country", "headquartersCountry", true)}
            placeholder="Select country"
            data={countryOptions}
            value={formData.headquartersCountry}
            onChange={(value) => {
              updateForm("headquartersCountry", value);
              updateForm("country", value);
              if (value?.trim().toLowerCase() !== "usa" && value?.trim().toLowerCase() !== "united states") {
                updateForm("state", "");
              }
            }}
            searchable
            disabled={isFieldLocked("headquartersCountry")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label={renderLabel("Phone Number", "phoneNumber", true)}
            placeholder="hidden"
            styles={{
              ...fieldStyles,
              input: {
                display: "none",
              },
              wrapper: {
                display: "none",
              },
            }}
            inputContainer={() => (
              <PhoneInput
                key={phoneDefaultCountry}
                defaultCountry={phoneDefaultCountry}
                value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
                onChange={(phone, meta) => {
                  const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                  const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                  updateForm("phoneCountryCode", countryCode);
                  updateForm("phoneNumber", nationalNumber);
                }}
                placeholder="123-456-7890"
                style={{
                  width: "100%",
                }}
                inputStyle={{
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderLeft: "none",
                  borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                  outline: "none",
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    height: "36px",
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                    padding: "0 12px",
                    backgroundColor: "white",
                  },
                }}
              />
            )}
          />
        </Grid.Col>

        {isUSA && (
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Select
              styles={fieldStyles}
              label={renderLabel("State", "state", true)}
              placeholder="Select state"
              data={THE_KYB_US_STATE_CODES.map((item) => ({
                value: item.name,
                label: item.name,
              }))}
              value={formData.state || null}
              onChange={(value) => updateForm("state", value)}
            />
          </Grid.Col>
        )}
      </Grid>
      <Grid gutter="md" mb="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Select
            styles={fieldStyles}
            label={renderLabel("Business structure", "verificationBusinessType", false)}
            placeholder="Select type"
            data={VERIFICATION_BUSINESS_TYPE_SELECT_DATA}
            value={businessStructureSelectValue}
            onChange={(value) => {
              if (!value) {
                updateForm("verificationBusinessType", "");
                return;
              }
              if (value === "OTHER") {
                updateForm("verificationBusinessType", "OTHER");
                return;
              }
              updateForm("verificationBusinessType", value);
            }}
            disabled={isFieldLocked("verificationBusinessType")}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Select
            styles={fieldStyles}
            label={renderLabel("Paidup capital Band in USD $ (Optional)", "paidupCapitalBand", false)}
            placeholder="Select band"
            data={PAIDUP_CAPITAL_BAND_SELECT_DATA}
            value={formData.paidupCapitalBand || ""}
            onChange={(value) => updateForm("paidupCapitalBand", value || "")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Select
            styles={fieldStyles}
            label={renderLabel("Turnover Band in USD $ (Optional)", "turnoverBand", false)}
            placeholder="Select band"
            data={TURNOVER_BAND_SELECT_DATA}
            value={formData.turnoverBand || ""}
            onChange={(value) => updateForm("turnoverBand", value || "")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          {businessStructureSelectValue === "OTHER" && (
            <TextInput
              styles={fieldStyles}
              label={renderLabel("Describe your business structure", "verificationBusinessType", true)}
              placeholder="e.g. Cooperative, trust, joint venture…"
              value={businessStructureOtherText}
              onChange={(e) => updateForm("verificationBusinessType", e.currentTarget.value.slice(0, 200))}
              disabled={isFieldLocked("verificationBusinessType")}
              maxLength={200}
            />
          )}
        </Grid.Col>
      </Grid>

      {/* Company Website and Phone Number are positioned above in the 3-column rows */}
    </Box>
  );
}
